<template>
    <div class="form-control w-full">
        <!-- Label -->
        <label
            class="label"
            :for="name">
            <span class="label-text">
                {{ label }}
                <span
                    v-if="required"
                    class="text-error-500 ml-1">
                    *
                </span>
            </span>
        </label>

        <!-- File Input Box -->
        <label
            :for="name"
            class="w-full border-2 border-dashed border-info-500 p-8 text-center rounded-lg text-info-500 cursor-pointer hover:text-info-600 hover:border-info-600">
            <div class="flex flex-col gap-2 items-center align-middle min-h-28">
                <template v-if="cFileName.length > 0">
                    <hi-folder-icon class="h-10 w-10" />
                    <p
                        v-for="(n, index) in cFileName"
                        :key="index"
                        class="label truncate max-w-full">
                        {{ n }}
                    </p>
                </template>

                <template v-else>
                    <hi-folder-plus-icon class="h-10 w-10" />
                    <p>
                        {{ subtitle }}
                    </p>
                    <p class="text-gray-500">
                        Dung lượng tối đa 10 MB
                    </p>
                </template>

                <input
                    :id="name"
                    ref="fileInput"
                    type="file"
                    class="hidden"
                    :name="name"
                    :accept="accept"
                    :multiple="multiple"
                    :disabled="disabled"
                    @change="onFileChange" >
            </div>
        </label>

        <!-- Error Message -->
        <span
            v-if="errorMessage"
            class="label-text-alt text-rose-500 mt-2">
            {{ errorMessage }}
        </span>

        <!-- Description -->
        <span
            v-if="description"
            class="label-text-alt mt-2">
            {{ description }}
        </span>

        <slot name="bottom" />
    </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from 'vue'
import { useField, useFieldError } from 'vee-validate'

const props = defineProps({
  modelValue: {
    type: [Array<File>, File, String],
    required: true
  },
  label: String,
  placeholder: String,
  description: String,
  prop: String,
  name: String,
  subtitle: {
    type: String,
    default: 'Chọn 1 tệp CSV từ máy để tải lên'
  },
  rules: Object,
  size: String,
  color: String,
  accept: String,
  multiple: Boolean,
  required: Boolean,
  disabled: Boolean,
  readonly: Boolean,
  fileName: {
    type: [Array<String>, String, null, undefined]
  }
})

const emits = defineEmits(['update:modelValue'])

const { modelValue, prop, name, rules, multiple, disabled, required, readonly, fileName } = toRefs(props)
const fileInput = templateRef<HTMLInputElement | null>('fileInput', null)
let field: any
let errorMessage: undefined | ComputedRef
const cProp = computed(() => prop?.value)

const setFields = (prop: any) => {
  if (typeof prop === 'string' && prop !== '') {
    field = useField(prop, undefined, {
      label: name?.value || prop,
      validateOnValueUpdate: true,
      validateOnMount: false
    })
    errorMessage = useFieldError(cProp.value)
  }
}

watch(
  () => cProp.value,
  (val: any) => {
    setFields(val)
  }
)

onMounted(() => {
  setFields(cProp.value)
})

const cValue = computed({
  get () {
    return modelValue.value
  },
  set (val) {
    field?.setValue(val)
    emits('update:modelValue', val)
  }
})

const cFileName = computed(() => {
  if (cValue.value && Array.isArray(cValue.value) && cValue.value.length) {
    if (cValue.value.length === 1) {
      if (!cValue.value?.[0]?.name) {
        return []
      }
    }
    return cValue.value.map((item) => item.name)
  }

  if (cValue.value?.name) {
    return [cValue.value.name]
  }

  if (Array.isArray(fileName.value)) {
    return [fileName.value]
  }

  return []
})

watch(
  () => cValue.value,
  (val: any) => {
    field?.setValue(val)
  }
)

watch(
  () => modelValue.value,
  (val: File | File[] | string) => {
    if (fileInput.value) {
      if (val === undefined || val === null || val === '') {
        fileInput.value.value = ''
      }
    }
  }
)

const onFileChange = (e: any) => {
  const target = e.target

  if (target && target.files) {
    if (multiple.value) {
      cValue.value = target.files
    } else {
      cValue.value = target.files[0]
    }
  }
}

onMounted(() => {
  if (modelValue !== undefined && modelValue.value !== undefined) {
    field?.setValue(modelValue.value)
  }
})
</script>

<style scoped>
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
